import React from "react";
// Vee Rule Builder Modal

// Default
import { useEffect, useMemo, useState } from "react";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import Stack from "@mui/material/Stack";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { Check, Help } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";

// CodeMirror
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";

const AllTabs = [
  { name: "Details", value: "1" },
  { name: "Actions", value: "2" },
];

//  convert to object for API
const getHeadersObject = (apiHeaderArray: any) => {
  const headersObj: { [key: string]: string } = {};
  apiHeaderArray.forEach(
    ({ header, value }: { header: string; value: string }) => {
      if (header.trim()) {
        headersObj[header.trim()] = value;
      }
    }
  );
  return headersObj;
};

const CommonRuleModal = ({
  onSave,
  onClose,
  modalIsOpen,
  data,
}: {
  onSave: (node: any, isNew: boolean) => void;
  onClose: () => void;
  modalIsOpen: boolean;
  data: any;
}) => {
  const [tabValue, setTabValue] = useState("1");
  const { title, action, bgColor, nodeData, isNew } = data;

  const [tabs, setTabs] = useState(["1"]);

  const handleTabChange = (e: any, tab: string) => {
    setTabValue(tab);
  };

  const [anApiCallData, setAnApiCallData] = useState({
    enableProxy: false,
    ignoreRequestBody: false,
    maxInMemoryBufferSizeInKb: 0,
    maxParallelRequestsCount: 0,
    parseToPlainText: false,
    readTimeoutMs: 0,
    requestMethod: "",
    restEndpointUrlPattern: "",
    useSimpleClientHttpFactory: false,
    useSystemProxyProperties: false,
  });

  const [isAnApiCall, setIsAnApiCall] = useState(false);
  const [editedCodeBlock, setEditedCodeBlock] = useState<string | null>(null);

  const renderTabs = useMemo(() => {
    return AllTabs.filter((tb) => tabs?.includes(tb.value));
  }, [tabs]);

  const [name, setName] = useState("");

  const [apiHeaderArray, setApiHeaderArray] = useState<any[]>([]);

  // console.log(getHeadersObject(apiHeaderArray), "");

  useEffect(() => {
    setName(title);
    let ShowTabs = ["1"];
    if (nodeData?.configuration?.tbelScript) {
      setEditedCodeBlock(nodeData?.configuration?.tbelScript);
    } else {
      setEditedCodeBlock(null);
    }

    if (
      nodeData?.type == "org.thingsboard.rule.engine.rest.TbRestApiCallNode"
    ) {
      setIsAnApiCall(true);
      setAnApiCallData({
        enableProxy: nodeData?.configuration?.enableProxy || false,
        ignoreRequestBody: nodeData?.configuration?.ignoreRequestBody || false,
        maxInMemoryBufferSizeInKb:
          nodeData?.configuration?.maxInMemoryBufferSizeInKb || 0,
        maxParallelRequestsCount:
          nodeData?.configuration?.maxParallelRequestsCount || 0,
        parseToPlainText: nodeData?.configuration?.parseToPlainText || false,
        readTimeoutMs: nodeData?.configuration?.readTimeoutMs || 0,
        requestMethod: nodeData?.configuration?.requestMethod || "",
        restEndpointUrlPattern:
          nodeData?.configuration?.restEndpointUrlPattern || "",
        useSimpleClientHttpFactory:
          nodeData?.configuration?.useSimpleClientHttpFactory || false,
        useSystemProxyProperties:
          nodeData?.configuration?.useSystemProxyProperties || false,
      });

      const apiHeaderArray = Object.entries(
        nodeData?.configuration?.headers || {}
      ).map(([key, value]) => ({
        header: key,
        value: value,
      }));

      setApiHeaderArray(apiHeaderArray);
    } else {
      setIsAnApiCall(false);
    }
    setTabs(ShowTabs);
  }, [data]);

  const HandleSave = () => {
    if (nodeData?.isDummy) {
      onClose();
      return;
    }

    let node = nodeData;

    node.name = name;

    if (editedCodeBlock) {
      node.configuration.tbelScript = editedCodeBlock;
    }

    if (isAnApiCall) {
      node.configuration = {
        ...node.configuration,
        ...anApiCallData,
        headers: getHeadersObject(apiHeaderArray),
      };
    }
    console.log(node, "---saved node---");
    onSave(node, isNew);
  };

  // Update a field
  const handleChangeHeader = (
    index: number,
    field: string,
    newValue: string
  ) => {
    const updatedArray = [...apiHeaderArray];
    updatedArray[index][field] = newValue;
    setApiHeaderArray(updatedArray);
  };

  // Add new header row
  const handleAddHeader = () => {
    setApiHeaderArray([...apiHeaderArray, { header: "", value: "" }]);
  };

  // Remove header row
  const handleRemoveHeader = (index: number) => {
    const updatedArray = apiHeaderArray.filter((_, i) => i !== index);
    setApiHeaderArray(updatedArray);
  };

  const handleChangeAnApiCallData = (field: string, newValue: string) => {
    setAnApiCallData((old) => ({ ...old, [field]: newValue }));
  };

  return (
    <Dialog
      onClose={onClose}
      open={modalIsOpen ? true : false}
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          width: "800px",
          maxWidth: "none",
          minHeight: "300px",
        },
      }}>
      {/* header */}
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        style={{
          backgroundColor: bgColor || "darkslateblue",
          color: "#fff",
          paddingRight: 10,
          position: "relative",
        }}>
        <Box>
          <DialogTitle sx={{ m: 0, pt: 2, pb: 0, px: 2 }}>{title}</DialogTitle>
          <DialogTitle sx={{ m: 0, pt: 0, pb: 2, px: 2, fontSize: 14 }}>
            Action : {action}
          </DialogTitle>
        </Box>
        <Box>
          <IconButton sx={{ marginRight: "10px" }}>
            <Help style={{ color: "#fff" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: -14,
            right: 7,
            display: "flex",
            gap: 0.5,
            zIndex: 10,
          }}>
          <IconButton
            onClick={HandleSave}
            sx={{
              backgroundColor: bgColor || "darkslateblue",
              p: 1,
              "&:hover": {
                background: bgColor || "darkslateblue",
              },
            }}>
            <Check style={{ color: "#fff" }} />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              backgroundColor: bgColor || "darkslateblue",
              p: 1,
              "&:hover": {
                background: bgColor || "darkslateblue",
              },
            }}>
            <CloseIcon style={{ color: "#fff" }} />
          </IconButton>
        </Box>
      </Stack>

      {/* content */}
      <DialogContent
        dividers
        sx={{ p: 0, maxHeight: "600px", overflow: "auto" }}>
        <TabContext value={tabValue}>
          {/* tabs */}
          <Box sx={{ borderBottom: 0.5, borderColor: "lightgray", p: 0 }}>
            <TabList
              onChange={handleTabChange}
              sx={{
                ".Mui-selected": {
                  color: bgColor || "darkslateblue",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: bgColor || "darkslateblue",
                },
              }}>
              {renderTabs?.map((tt, index) => {
                return (
                  <Tab
                    key={index}
                    value={tt.value}
                    label={tt.name}
                    sx={{ textTransform: "none", fontSize: 14 }}
                  />
                );
              })}
            </TabList>
          </Box>

          {/* Details tab content */}
          <TabPanel value="1" sx={{ py: 1 }}>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              variant="standard"
              label="Name"
              required
            />

            {editedCodeBlock && (
              <Stack gap={"5px"} marginTop={"15px"} marginBottom={"15px"}>
                <Typography>Script</Typography>
                <CodeMirror
                  value={editedCodeBlock}
                  height="300px"
                  extensions={[javascript()]}
                  theme="light"
                  editable={true} // Make it read-only if you're not editing
                  style={{
                    borderRadius: "5px",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    backgroundColor: "#282c34",
                    color: "#000",
                    overflow: "hidden",
                  }}
                  onChange={(e) => {
                    setEditedCodeBlock(e);
                  }}
                />
              </Stack>
            )}

            {isAnApiCall && (
              <Stack gap={"5px"} marginTop={"15px"} marginBottom={"15px"}>
                <TextField
                  size="small"
                  value={anApiCallData.restEndpointUrlPattern}
                  onChange={(e) =>
                    handleChangeAnApiCallData(
                      "restEndpointUrlPattern",
                      e.target.value
                    )
                  }
                  fullWidth
                  variant="standard"
                  label="Endpoint URL pattern"
                  helperText="Use $(metadataKey) for value from metadata, $[messageKey] for value from message body."
                  required
                />
                <TextField
                  size="small"
                  select
                  label="Request metod"
                  value={anApiCallData.requestMethod}
                  onChange={(e) =>
                    handleChangeAnApiCallData("requestMethod", e.target.value)
                  }
                  variant="standard"
                  required>
                  <MenuItem value={"POST"}>POST</MenuItem>
                  <MenuItem value={"GET"}>GET</MenuItem>
                  <MenuItem value={"PUT"}>PUT</MenuItem>
                  <MenuItem value={"DELETE"}>DELETE</MenuItem>
                  <MenuItem value={"PATCH"}>PATCH</MenuItem>
                </TextField>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anApiCallData.enableProxy}
                      onChange={(e) =>
                        handleChangeAnApiCallData(
                          "enableProxy",
                          e.target.checked.toString()
                        )
                      }
                    />
                  }
                  label="Enable proxy"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anApiCallData.useSimpleClientHttpFactory}
                      onChange={(e) =>
                        handleChangeAnApiCallData(
                          "useSimpleClientHttpFactory",
                          e.target.checked.toString()
                        )
                      }
                    />
                  }
                  label="Use simple client HTTP factory"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anApiCallData.parseToPlainText}
                      onChange={(e) =>
                        handleChangeAnApiCallData(
                          "parseToPlainText",
                          e.target.checked.toString()
                        )
                      }
                    />
                  }
                  label="Parse to plain text"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anApiCallData.ignoreRequestBody}
                      onChange={(e) =>
                        handleChangeAnApiCallData(
                          "ignoreRequestBody",
                          e.target.checked.toString()
                        )
                      }
                    />
                  }
                  label="Without request body"
                />

                <TextField
                  size="small"
                  value={anApiCallData.readTimeoutMs}
                  onChange={(e) =>
                    handleChangeAnApiCallData("readTimeoutMs", e.target.value)
                  }
                  type="number"
                  fullWidth
                  variant="standard"
                  label="Read timeout in millis"
                  helperText="value of O means Infinite timeout"
                />
                <TextField
                  size="small"
                  value={anApiCallData.maxParallelRequestsCount}
                  onChange={(e) =>
                    handleChangeAnApiCallData(
                      "maxParallelRequestsCount",
                      e.target.value
                    )
                  }
                  type="number"
                  fullWidth
                  variant="standard"
                  label="Max number of parallel requests"
                  helperText="The value of 0 specifies no limit in parallel processing"
                />
                <TextField
                  size="small"
                  value={anApiCallData.maxInMemoryBufferSizeInKb}
                  onChange={(e) =>
                    handleChangeAnApiCallData(
                      "maxInMemoryBufferSizeInKb",
                      e.target.value
                    )
                  }
                  type="number"
                  fullWidth
                  variant="standard"
                  label="Max response size (in KB)"
                  helperText="The maximum amount of memory allocated for buffering data when decoding or encoding HTTP messages, such JSON payloads"
                />
                <Stack>
                  <Typography variant="inherit">Headers</Typography>
                  <FormHelperText>
                    {
                      "Use ${metadatakey} for value from metadata, $[messageKey] for value from message body in header/value fields"
                    }
                  </FormHelperText>

                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    marginBottom={1}>
                    {apiHeaderArray.map((row, index) => {
                      return (
                        <Box
                          flexDirection={"row"}
                          display={"flex"}
                          gap={2}
                          key={index}>
                          <TextField
                            size="small"
                            value={row.header}
                            onChange={(e) =>
                              handleChangeHeader(
                                index,
                                "header",
                                e.target.value
                              )
                            }
                            fullWidth
                            variant="standard"
                            label="Header"
                            required
                          />
                          <TextField
                            size="small"
                            value={row.value}
                            onChange={(e) =>
                              handleChangeHeader(index, "value", e.target.value)
                            }
                            fullWidth
                            variant="standard"
                            label="Value"
                            required
                          />
                          <IconButton onClick={() => handleRemoveHeader(index)}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                    <Button
                      variant="contained"
                      onClick={handleAddHeader}
                      sx={{
                        textTransform: "none",
                        width: 5,
                      }}
                      startIcon={<AddIcon />}>
                      Add
                    </Button>
                  </Box>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    variant="standard"
                    label="Rule Node Description"
                  />
                </Stack>
              </Stack>
            )}
          </TabPanel>
        </TabContext>
      </DialogContent>
    </Dialog>
  );
};

export default CommonRuleModal;
